import { NullableRecord } from "../types";

export function removeDuplicates<T>(arrayWithDuplicates: T[]): T[] {
    return Array.from(new Set(arrayWithDuplicates));
}

export function firstElement<T>(array: T[] | undefined): T | undefined {
    return array?.[0];
}

export function removeDuplicatesOrUndefined<T>(arrayWithDuplicates: T[] | undefined): T[] | undefined {
    if (!arrayWithDuplicates) return undefined;
    return removeDuplicates(arrayWithDuplicates);
}

export function splitArray<T>(
    elements: T[] | undefined,
    filter: (element: T) => boolean
): [T[] | undefined, T[] | undefined] {
    const filtered: T[] = [];
    const filteredOut: T[] = [];

    if (!elements) return [undefined, undefined];

    for (const element of elements) {
        const isWith = filter(element);
        if (isWith) {
            filtered.push(element);
        } else {
            filteredOut.push(element);
        }
    }
    return [filtered, filteredOut];
}

export function removeDuplicatesByProperty<T>(arrayWithDuplicates: T[], key: keyof T): T[] {
    return Array.from(new Map(arrayWithDuplicates.map((d) => [d[key], d])).entries()).map(([, data]) => data);
}

export function removeDuplicatesWithFunction<T>(arrayWithDuplicates: T[], getKey: (t: T) => string | number): T[] {
    return Array.from(new Map(arrayWithDuplicates.map((d) => [getKey(d), d])).entries()).map(([, data]) => data);
}

export function combineCollections<T>(collections: (T[] | Set<T> | undefined)[]): T[] {
    const combined: T[] = [];

    for (const collection of collections) {
        if (collection === undefined) continue;
        if (Array.isArray(collection)) {
            combined.push(...collection);
        } else if (collection instanceof Set) {
            combined.push(...collection);
        }
    }

    return combined;
}

export function deepCompareArrays<T extends string | number>(array1: T[], array2: T[]) {
    return deepCompareSets(new Set(array1), new Set(array2));
}

type ValidType = string | number | undefined;
export function deepCompareMaps<K extends string | number, T extends ValidType>(m1: Map<K, T>, m2: Map<K, T>) {
    if (m1.size !== m2.size) {
        return false;
    }

    for (const [key, val] of m1.entries()) {
        if (!m2.has(key)) return false;
        if (val !== m2.get(key)) return false;
    }
    return true;
}

export function deepCompareArraysWithFunction<T>(array1: T[], array2: T[], getKey: (t: T) => string | number) {
    if (array1.length !== array2.length) {
        return false;
    }
    const array1Map = new Map(array1.map((item) => [getKey(item), item]));

    for (const item of array2) {
        const key = getKey(item);
        if (!array1Map.has(key)) {
            return false;
        }
    }

    return true;
}

export function deepCompareSets<T extends string | number>(set1: Set<T>, set2: Set<T>): boolean {
    if (set1.size !== set2.size) {
        return false;
    }

    for (const item of set1) {
        if (!set2.has(item)) {
            return false;
        }
    }

    return true;
}

function isObjectLike(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

/**
 * Deep semantic equality for plain objects/arrays/primitives.
 * Uses Object.is for primitive equality and recursively compares nested members.
 */
export function deepEqual(a: unknown, b: unknown): boolean {
    if (Object.is(a, b)) return true;
    if (typeof a !== typeof b) return false;

    if (Array.isArray(a) || Array.isArray(b)) {
        if (!Array.isArray(a) || !Array.isArray(b)) return false;
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i += 1) {
            if (!deepEqual(a[i], b[i])) return false;
        }
        return true;
    }

    if (!isObjectLike(a) || !isObjectLike(b)) return false;

    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;

    for (const key of aKeys) {
        if (!(key in b)) return false;
        if (!deepEqual(a[key], b[key])) return false;
    }

    return true;
}

// empty fn used as placeholder
export function doNothing() {
    return;
}

export const emptyPromise = new Promise<undefined>((resolve) => resolve(undefined));

export function customEmptyPromise<T>(resolveType: T) {
    return new Promise<T>((resolve) => resolve(resolveType));
}

export function allKeysTruthy<T>(obj: NullableRecord<T>, keys: (keyof T)[]): boolean {
    return keys.every((key) => !!obj[key]);
}

export function allKeysDefined<T>(obj: NullableRecord<T>, keys: (keyof T)[]): boolean {
    return keys.every((key) => obj[key] !== undefined);
}

export function filterAllKeysDefined<T>(obj: NullableRecord<T>): obj is T {
    return allKeysDefined(obj, Object.keys(obj) as (keyof T)[]);
}

export function filterUndefined<T>(val: T | undefined): val is T {
    return val !== undefined;
}

export function filterTruthy<T>(val: T | undefined | null): val is T {
    return !!val;
}

export function isDefined<T>(val: T | undefined | null): val is T {
    return val !== undefined && val !== null;
}

// Filter out entries with missing fields
export function filterNullableRecord<T>(obj: NullableRecord<T>): obj is T {
    return Object.values(obj).every((value) => value !== undefined);
}

// Used exclusively for force asserting a type from a partial object
export function assetPartialRecord<T>(obj: Partial<T>): obj is T {
    return Object.values(obj).every((value) => value !== undefined);
}
export function findFirstIndex<T>(array: T[], condition: (element: T) => boolean): number {
    for (let i = 0; i < array.length; i++) {
        if (condition(array[i])) {
            return i;
        }
    }
    return -1; // Return -1 if no element satisfies the condition
}

export function findLastIndex<T>(array: T[], condition: (element: T) => boolean): number {
    for (let i = array.length - 1; i >= 0; i--) {
        if (condition(array[i])) {
            return i;
        }
    }
    return -1; // Return -1 if no element satisfies the condition
}

export function unique<T>(arr: T[]): T[] {
    return Array.from(new Set(arr));
}

export function uniqueBy<T, K>(arr: T[], keyFn: (item: T) => K): T[] {
    const seen = new Set<K>();
    const result: T[] = [];
    for (const item of arr) {
        const key = keyFn(item);
        if (seen.has(key)) continue;
        seen.add(key);
        result.push(item);
    }
    return result;
}

export function groupArrayElementsBy<T, K>(array: T[], keySelector: (item: T) => K): Map<K, T[]> {
    return array.reduce((resultMap, item) => {
        const key = keySelector(item);
        const prevValue = resultMap.get(key) || [];
        resultMap.set(key, [...prevValue, item]);
        return resultMap;
    }, new Map<K, T[]>());
}

export function filterMap<KeyType, ValueType>(
    map: Map<KeyType, ValueType>,
    filter: (val: ValueType) => boolean
): Map<KeyType, ValueType> {
    const entries = Array.from(map.entries()).filter(([, val]) => filter(val));
    return new Map(entries);
}

export function filterMapByKey<KeyType, ValueType>(
    map: Map<KeyType, ValueType>,
    filter: (val: KeyType) => boolean
): Map<KeyType, ValueType> {
    const entries = Array.from(map.entries()).filter(([key]) => filter(key));
    return new Map(entries);
}

export function filterMapValues<KeyType, ValueType>(
    map: Map<KeyType, ValueType>,
    filter: (val: ValueType) => boolean
): ValueType[] {
    return Array.from(map.values()).filter((val) => filter(val));
}

export async function asyncForEach<Item, Params, T>(
    params: Params[],
    asyncFn: (param: Params) => Promise<T>,
    getParams?: (raw: Item) => Params
): Promise<T[]> {
    const results: T[] = [];
    for (const param of params) {
        const result = await asyncFn(param);

        results.push(result);
    }
    return results;
}

export async function retryAsync<T>(fn: () => Promise<T>, maxRetries: number, intervalMs = 1000): Promise<T> {
    let lastError: Error | undefined;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            const result = await fn();
            return result;
        } catch (error) {
            lastError = error as Error;
            if (attempt < maxRetries - 1) {
                // Linear backoff: wait intervalMs * (attempt + 1) milliseconds
                await new Promise((resolve) => setTimeout(resolve, intervalMs * (attempt + 1)));
            }
        }
    }

    throw lastError || new Error("Operation failed after max retries");
}

export function mapValues<T extends object, U>(
    obj: T,
    mapper: (value: T[keyof T], key: keyof T) => U
): { [K in keyof T]: U } {
    return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, mapper(value as T[keyof T], key as keyof T)])
    ) as { [K in keyof T]: U };
}

export function mapRecordArray<T, U>(obj: Record<string, T[]>, fn: (value: T) => U): Record<string, U[]> {
    return mapValues(obj, (arr) => arr.map(fn));
}

export function mapEntries<K extends string | number | symbol, V, K2 extends string | number | symbol, V2>(
    obj: Record<K, V>,
    fn: (key: K, value: V) => [K2, V2]
): Record<K2, V2> {
    return Object.fromEntries(Object.entries(obj).map(([key, value]) => fn(key as K, value as V))) as Record<K2, V2>;
}

export function mapDefined<T, U>(arr: T[], fn: (value: T, index: number) => U | undefined): U[] {
    return arr.map(fn).filter(filterTruthy);
}

export function toTitleCase(str: string): string {
    if (!str) return "";
    return str
        .trim()
        .split(/\s+/)
        .map((word) => (word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : ""))
        .join(" ");
}

export function hasObjectChanged<T extends object>(a: T, b: T, keys: (keyof T)[]): boolean {
    return keys.some((key) => a[key] !== b[key]);
}
